import { useState, useEffect, useMemo } from "react";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import DocumentCard from "components/DocumentCard";
import MissionDocumentViewerDialog from "components/MissionDocumentViewerDialog";

import { getUser, getPilotDocuments } from "../Users/services/graphqlService";
import {
  mapToPilotProfileDisplay,
  mapToPilotAccountDisplay,
  pilotProfileDisplayName,
} from "../pilotProfileDisplay";
import {
  parseMissionDocumentsResponse,
  downloadMissionDocument,
} from "utils/missionDocuments";

const MISSING = "--";

const PROFILE_GROUPS = [
  {
    key: "contact",
    title: "Contact",
    icon: "contact_mail",
    color: "info",
    fields: [
      { label: "First name", key: "firstName", source: "profile" },
      { label: "Last name", key: "lastName", source: "profile" },
      { label: "Email", key: "email", source: "profile" },
      { label: "Phone", key: "phone", source: "profile" },
      { label: "Mobile", key: "mobile", source: "account" },
    ],
  },
  {
    key: "location",
    title: "Location",
    icon: "location_on",
    color: "success",
    fields: [
      { label: "City", key: "city", source: "profile" },
      { label: "State", key: "state", source: "profile" },
      { label: "Home airport", key: "homeAirport", source: "profile" },
    ],
  },
  {
    key: "aviation",
    title: "Aviation",
    icon: "flight",
    color: "warning",
    fields: [
      { label: "Aircraft types", key: "aircraftTypes", source: "profile" },
      { label: "PIC hours", key: "picTime", source: "profile" },
      {
        label: "Max distance willing to fly",
        key: "willingToFlyDistance",
        source: "profile",
      },
    ],
  },
  {
    key: "about",
    title: "About",
    icon: "volunteer_activism",
    color: "secondary",
    fields: [
      {
        label: "Additional information",
        key: "additionalInfo",
        source: "profile",
        multiline: true,
      },
      {
        label: "How did you hear about us?",
        key: "hearAboutUs",
        source: "profile",
        multiline: true,
      },
      {
        label: "Why volunteer?",
        key: "whyVolunteer",
        source: "profile",
        multiline: true,
      },
    ],
  },
  {
    key: "account",
    title: "Account",
    icon: "badge",
    color: "dark",
    fields: [
      { label: "Profile ID", key: "profileId", source: "account", mono: true },
      { label: "Status", key: "status", source: "account" },
      { label: "Role", key: "role", source: "account" },
      {
        label: "Associations",
        key: "associations",
        source: "account",
        multiline: true,
      },
      { label: "Employee ID", key: "employeeId", source: "account" },
      { label: "Locale", key: "locale", source: "account" },
      { label: "Timezone", key: "timezone", source: "account" },
      { label: "Last login", key: "lastLogin", source: "account" },
      { label: "Created", key: "createdAt", source: "account" },
      { label: "Last updated", key: "updatedAt", source: "account" },
    ],
  },
  {
    key: "optional",
    title: "Optional",
    icon: "note_alt",
    color: "dark",
    fields: [
      { label: "Notes", key: "notes", source: "profile", multiline: true },
    ],
  },
];

const QUICK_STATS = [
  { icon: "flight_takeoff", label: "Home airport", key: "homeAirport" },
  { icon: "schedule", label: "PIC hours", key: "picTime" },
  { icon: "airplanemode_active", label: "Aircraft", key: "aircraftTypes" },
  { icon: "social_distance", label: "Max distance", key: "willingToFlyDistance" },
];

function formatFieldValue(value) {
  if (value === undefined || value === null) return "";
  const text = Array.isArray(value)
    ? value.join(", ").trim()
    : String(value).trim();
  return text;
}

function hasValue(value) {
  return Boolean(formatFieldValue(value));
}

function panelSx() {
  return (theme) => ({
    borderRadius: "0.875rem",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 10px rgba(15, 34, 58, 0.04)",
    height: "100%",
  });
}

function StatTile({ icon, label, value }) {
  const display = formatFieldValue(value) || MISSING;
  return (
    <SoftBox
      p={1.5}
      sx={(theme) => ({
        borderRadius: "0.75rem",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "rgba(99, 110, 255, 0.04)",
        minWidth: 0,
        height: "100%",
      })}
    >
      <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
        <Icon sx={{ fontSize: "1rem !important", color: "info.main" }}>
          {icon}
        </Icon>
        <SoftTypography variant="caption" color="secondary" fontWeight="medium">
          {label}
        </SoftTypography>
      </SoftBox>
      <SoftTypography
        variant="button"
        fontWeight="bold"
        color="dark"
        sx={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={display}
      >
        {display}
      </SoftTypography>
    </SoftBox>
  );
}

function DetailRow({ label, value, multiline = false, mono = false }) {
  const display = formatFieldValue(value) || MISSING;
  const isMissing = display === MISSING;

  return (
    <SoftBox
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      gap={{ xs: 0.25, sm: 1.5 }}
      py={0.875}
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none", pb: 0 },
      })}
    >
      <SoftTypography
        variant="caption"
        color="secondary"
        fontWeight="medium"
        sx={{ flex: "0 0 38%", minWidth: 120 }}
      >
        {label}
      </SoftTypography>
      <SoftTypography
        variant="body2"
        color={isMissing ? "secondary" : "dark"}
        fontWeight={isMissing ? "regular" : "medium"}
        sx={{
          flex: 1,
          minWidth: 0,
          whiteSpace: multiline ? "pre-wrap" : "normal",
          wordBreak: "break-word",
          fontStyle: isMissing ? "italic" : "normal",
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: mono ? "0.75rem" : undefined,
        }}
      >
        {display}
      </SoftTypography>
    </SoftBox>
  );
}

function GroupPanel({ title, icon, color = "info", children, action }) {
  return (
    <SoftBox p={2} sx={panelSx()}>
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        mb={1.25}
      >
        <SoftBox display="flex" alignItems="center" gap={1}>
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={32}
            height={32}
            borderRadius="md"
            sx={({ palette, functions: { linearGradient } }) => {
              const gradient = palette.gradients?.[color] ?? palette.gradients.info;
              return {
                backgroundImage: linearGradient(gradient.main, gradient.state),
                color: palette.white.main,
              };
            }}
          >
            <Icon sx={{ fontSize: "1rem !important" }}>{icon}</Icon>
          </SoftBox>
          <SoftTypography variant="button" fontWeight="bold" color="dark">
            {title}
          </SoftTypography>
        </SoftBox>
        {action}
      </SoftBox>
      {children}
    </SoftBox>
  );
}

function ViewPilotProfileModal({
  open,
  onClose,
  profileId,
  idToken,
  task,
  fallbackName,
}) {
  const [userData, setUserData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documentsError, setDocumentsError] = useState(null);
  const [viewerDocument, setViewerDocument] = useState(null);
  const [documentActionId, setDocumentActionId] = useState(null);

  useEffect(() => {
    if (!open) {
      setUserData(null);
      setDocuments([]);
      setError(null);
      setDocumentsError(null);
      setViewerDocument(null);
      setDocumentActionId(null);
      setLoading(false);
      setDocumentsLoading(false);
      return;
    }

    if (!profileId || !idToken) {
      setUserData(null);
      setDocuments([]);
      setError(null);
      setDocumentsError(null);
      setLoading(false);
      setDocumentsLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setDocumentsLoading(true);
    setError(null);
    setDocumentsError(null);
    setUserData(null);
    setDocuments([]);

    getUser(profileId, idToken)
      .then((res) => {
        if (cancelled) return;
        setUserData(res?.data ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load pilot profile");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    getPilotDocuments(profileId, idToken)
      .then((res) => {
        if (cancelled) return;
        setDocuments(parseMissionDocumentsResponse(res?.data));
      })
      .catch((err) => {
        if (cancelled) return;
        setDocumentsError(err?.message || "Failed to load pilot documents");
        setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, profileId, idToken]);

  const profile = useMemo(() => {
    if (userData) return mapToPilotProfileDisplay(userData, null);
    return mapToPilotProfileDisplay(null, task);
  }, [userData, task]);

  const account = useMemo(
    () => mapToPilotAccountDisplay(userData, { pilotContext: true }),
    [userData],
  );

  const displayName = useMemo(() => {
    if (loading) return fallbackName || "Pilot profile";
    const fromFetched = pilotProfileDisplayName(profile, userData);
    if (fromFetched !== "Pilot profile") return fromFetched;
    return fallbackName || fromFetched;
  }, [loading, profile, userData, fallbackName]);

  const headerEmail = profile.email || account.email || "";

  const getFieldValue = (field) => {
    const source = field.source === "account" ? account : profile;
    return source?.[field.key];
  };

  const populatedGroups = useMemo(
    () =>
      PROFILE_GROUPS.map((group) => ({
        ...group,
        fields: group.fields.filter((field) => {
          const source = field.source === "account" ? account : profile;
          return hasValue(source?.[field.key]);
        }),
      })).filter((group) => group.fields.length > 0),
    [profile, account],
  );

  const handleClose = () => {
    setUserData(null);
    setDocuments([]);
    setError(null);
    setDocumentsError(null);
    setViewerDocument(null);
    onClose();
  };

  const handleViewDocument = (doc) => {
    if (!idToken || !(doc.key || doc.documentPath)) return;
    setViewerDocument(doc);
  };

  const handleDownloadDocument = async (doc) => {
    if (!idToken) return;
    const docId = doc.id || doc.timelineId || doc.documentPath;
    setDocumentActionId(docId);
    try {
      await downloadMissionDocument(doc, idToken);
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Download failed",
            message: err?.message ?? "Could not download document.",
          },
        }),
      );
    } finally {
      setDocumentActionId(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.45)" },
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 1040,
            maxHeight: "92vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 24px 48px rgba(15, 34, 58, 0.2)",
            borderRadius: 2,
          }}
        >
          <SoftBox
            px={2.5}
            py={2.5}
            sx={({
              functions: { rgba, linearGradient },
              palette: { gradients },
            }) => ({
              backgroundImage: linearGradient(
                rgba(gradients.info.main, 0.1),
                rgba(gradients.info.state, 0.04),
              ),
              borderBottom: "1px solid",
              borderColor: "divider",
            })}
          >
            <SoftBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <SoftBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={48}
                  height={48}
                  borderRadius="md"
                  flexShrink={0}
                  sx={({ palette, functions: { linearGradient } }) => ({
                    backgroundImage: linearGradient(
                      palette.gradients.info.main,
                      palette.gradients.info.state,
                    ),
                    color: palette.white.main,
                    boxShadow: "0 4px 14px rgba(0, 184, 217, 0.3)",
                  })}
                >
                  <Icon sx={{ fontSize: "1.625rem !important" }}>person</Icon>
                </SoftBox>
                <SoftBox minWidth={0}>
                  <SoftTypography
                    variant="h5"
                    fontWeight="bold"
                    color="dark"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={displayName}
                  >
                    {displayName}
                  </SoftTypography>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    gap={0.75}
                    flexWrap="wrap"
                    mt={0.25}
                  >
                    {headerEmail ? (
                      <SoftTypography variant="caption" color="secondary">
                        {headerEmail}
                      </SoftTypography>
                    ) : null}
                    {account.role ? (
                      <SoftBadge
                        variant="gradient"
                        color="info"
                        size="xs"
                        badgeContent={account.role}
                        container
                      />
                    ) : null}
                    {account.status ? (
                      <SoftBadge
                        variant="contained"
                        color={
                          String(account.status).toLowerCase() === "yes" ||
                          String(account.status).toLowerCase() === "active"
                            ? "success"
                            : "secondary"
                        }
                        size="xs"
                        badgeContent={account.status}
                        container
                      />
                    ) : null}
                    {loading ? (
                      <CircularProgress size={14} color="info" />
                    ) : null}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
              <SoftButton
                variant="text"
                color="secondary"
                iconOnly
                onClick={handleClose}
                sx={{ width: 36, height: 36, flexShrink: 0 }}
              >
                <Icon sx={{ fontSize: "1.5rem !important" }}>close</Icon>
              </SoftButton>
            </SoftBox>
          </SoftBox>

          <SoftBox px={2.5} py={2} sx={{ overflow: "auto", flex: 1 }}>
            {error ? (
              <SoftBox mb={2}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </SoftBox>
            ) : null}

            <Grid container spacing={1.5} mb={2}>
              {QUICK_STATS.map((stat) => (
                <Grid item xs={6} sm={3} key={stat.key}>
                  <StatTile
                    icon={stat.icon}
                    label={stat.label}
                    value={profile[stat.key]}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} mb={2}>
              {populatedGroups.map((group) => (
                <Grid item xs={12} md={6} key={group.key}>
                  <GroupPanel
                    title={group.title}
                    icon={group.icon}
                    color={group.color}
                  >
                    {group.fields.map((field) => (
                      <DetailRow
                        key={`${group.key}-${field.key}`}
                        label={field.label}
                        value={getFieldValue(field)}
                        multiline={field.multiline}
                        mono={field.mono}
                      />
                    ))}
                  </GroupPanel>
                </Grid>
              ))}
              {populatedGroups.length === 0 ? (
                <Grid item xs={12}>
                  <SoftTypography
                    variant="body2"
                    color="secondary"
                    fontStyle="italic"
                  >
                    No profile details available.
                  </SoftTypography>
                </Grid>
              ) : null}
            </Grid>

            <GroupPanel
              title="Documents"
              icon="folder"
              color="primary"
              action={
                documentsLoading ? (
                  <CircularProgress size={16} color="info" />
                ) : documents.length > 0 ? (
                  <SoftBadge
                    variant="contained"
                    color="info"
                    size="xs"
                    badgeContent={String(documents.length)}
                    container
                  />
                ) : null
              }
            >
              {documentsError ? (
                <Alert severity="warning" sx={{ mb: 0 }}>
                  {documentsError}
                </Alert>
              ) : documentsLoading ? (
                <SoftBox display="flex" alignItems="center" gap={1} py={2}>
                  <CircularProgress size={20} />
                  <SoftTypography variant="body2" color="text">
                    Loading documents…
                  </SoftTypography>
                </SoftBox>
              ) : documents.length === 0 ? (
                <SoftBox textAlign="center" py={3}>
                  <Icon
                    sx={{
                      fontSize: "2rem !important",
                      color: "text.secondary",
                      mb: 1,
                    }}
                  >
                    folder_open
                  </Icon>
                  <SoftTypography variant="body2" color="text">
                    No documents linked to this pilot profile yet.
                  </SoftTypography>
                </SoftBox>
              ) : (
                <Grid container spacing={1.5}>
                  {documents.map((doc, index) => {
                    const docKey =
                      doc.id ||
                      doc.timelineId ||
                      `${doc.documentPath}-${index}`;
                    const canAccess = idToken && (doc.key || doc.documentPath);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={docKey}>
                        <DocumentCard
                          doc={doc}
                          canAccess={canAccess}
                          isBusy={documentActionId === docKey}
                          onView={handleViewDocument}
                          onDownload={handleDownloadDocument}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </GroupPanel>
          </SoftBox>
        </Card>
      </Modal>

      <MissionDocumentViewerDialog
        open={!!viewerDocument}
        onClose={() => setViewerDocument(null)}
        document={viewerDocument}
        idToken={idToken}
      />
    </>
  );
}

export default ViewPilotProfileModal;
